/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	AuthErrorReason,
	clearStoredCredentials,
	isAuthErrorReason,
} from '../constants/authError.constant';

const AuthCallback = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [status, setStatus] = useState('Processing...');
	const [error, setError] = useState('');
	const apiBaseUrl = process.env.REACT_APP_BASE_API_URL || '';

	useEffect(() => {
		const handleCallback = async () => {
			// Any refusal shares one exit: wipe partial credentials and hand the
			// reason to /login/error, which owns all sign-in failure messaging.
			const failWith = (
				reason: AuthErrorReason,
				extraParams?: Record<string, string>
			) => {
				clearStoredCredentials();
				const params = new URLSearchParams({ reason, ...extraParams });
				navigate(`/login/error?${params.toString()}`, { replace: true });
			};

			try {
				// Get tokens from URL
				const accessToken = searchParams.get('access_token');
				const refreshToken = searchParams.get('refresh_token');
				const errorParam = searchParams.get('error');
				const reasonParam = searchParams.get('reason');

				// The server sends refusals straight to /login/error, but handle a
				// reason arriving here too rather than showing a generic message.
				if (reasonParam) {
					failWith(
						isAuthErrorReason(reasonParam) ? reasonParam : 'auth_failed',
						{
							...(searchParams.get('provider')
								? { provider: searchParams.get('provider') as string }
								: {}),
							...(searchParams.get('email')
								? { email: searchParams.get('email') as string }
								: {}),
							...(searchParams.get('deletedat')
								? {
										deletedat: searchParams.get(
											'deletedat'
										) as string,
									}
								: {}),
						}
					);
					return;
				}

				// Check for error
				if (errorParam) {
					failWith('auth_failed');
					return;
				}

				// Validate tokens
				if (!accessToken || !refreshToken) {
					failWith('missing_tokens');
					return;
				}

				setStatus('Fetching user profile...');

				try {
					const response = await fetch(`${apiBaseUrl}/auth/profile`, {
						method: 'GET',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							'Content-Type': 'application/json',
						},
					});

					// 401/403 here means the account was refused between the redirect
					// and this call — treat it as a denial, not a transient failure.
					if (response.status === 401 || response.status === 403) {
						failWith('account_inactive');
						return;
					}

					if (!response.ok) {
						failWith('profile_fetch_failed');
						return;
					}

					const result = await response.json();

					// Extract user data from response (adjust based on your API structure)
					const userData = result.user || result.data || result;

					if (!userData) {
						failWith('profile_fetch_failed');
						return;
					}

					setStatus('Storing credentials...');

					// Store tokens and user data in the SAME format as regular login
					localStorage.setItem('token', accessToken);
					localStorage.setItem('refreshtoken', refreshToken);
					localStorage.setItem('user', JSON.stringify(userData));

					setStatus('Login successful! Redirecting...');

					// Small delay to ensure localStorage is written
					await new Promise(resolve => setTimeout(resolve, 500));

					// Redirect with replace to prevent back navigation
					// navigate('/learnerDashboard', { replace: true });
					const role = userData?.rolename?.toLowerCase();

					// let target = '/guestDashboard';

					// switch (role) {
					//     case 'admin':
					//         target = '/adminDashboard';
					//         break;
					//     case 'master coach':
					//         target = '/masterCoachDashboard';
					//         break;
					//     case 'coach mentor':
					//         target = '/coachMentorDashboard';
					//         break;
					//     case 'coach':
					//         target = '/coachDashboard';
					//         break;
					//     case 'learner':
					//     case 'coachee':
					//         target = '/learnerDashboard';
					//         break;
					// }

					// navigate(target, { replace: true });
					navigate('/dashboard', { replace: true });
				} catch (fetchError) {
					console.error('Error fetching user profile:', fetchError);
					failWith('profile_fetch_failed');
				}
			} catch (err) {
				console.error('Auth callback error:', err);
				failWith('auth_failed');
			}
		};

		handleCallback();
	}, [searchParams, navigate, apiBaseUrl]);

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				minHeight: '100vh',
				background: 'white',
				fontFamily: 'Plus Jakarta Sans',
			}}
		>
			<div
				style={{
					textAlign: 'center',
					color: '#949494',
					padding: '40px',
					maxWidth: '400px',
				}}
			>
				{error ? (
					<>
						<div
							style={{
								width: '80px',
								height: '80px',
								borderRadius: '50%',
								background: 'rgba(255, 59, 48, 0.2)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								margin: '0 auto 20px',
								fontSize: '48px',
							}}
						>
							✕
						</div>
						<h1 style={{ fontSize: '28px', marginBottom: '10px' }}>
							Error
						</h1>
						<p style={{ fontSize: '16px', opacity: 0.9 }}>
							{error}
						</p>
						<p
							style={{
								fontSize: '14px',
								marginTop: '20px',
								opacity: 0.7,
							}}
						>
							Redirecting to login...
						</p>
					</>
				) : (
					<>
						<div
							style={{
								width: '80px',
								height: '80px',
								borderRadius: '50%',
								background: 'rgba(255, 255, 255, 0.2)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								margin: '0 auto 20px',
								animation: 'scaleIn 0.5s ease-out',
							}}
						>
							<svg
								viewBox="0 0 52 52"
								style={{ width: '50px', height: '50px' }}
							>
								<path
									d="M14 27l8 8 16-16"
									stroke="white"
									strokeWidth="3"
									strokeLinecap="round"
									strokeLinejoin="round"
									fill="none"
								/>
							</svg>
						</div>
						<h1 style={{ fontSize: '28px', marginBottom: '10px' }}>
							{status}
						</h1>
						<div
							style={{
								width: '20px',
								height: '20px',
								border: '3px solid rgba(255, 255, 255, 0.3)',
								borderTopColor: 'white',
								borderRadius: '50%',
								display: 'inline-block',
								animation: 'spin 0.8s linear infinite',
								marginTop: '10px',
							}}
						/>
					</>
				)}
			</div>
		</div>
	);
};

export default AuthCallback;
